import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, APP_INITIALIZER, NO_ERRORS_SCHEMA, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { ServiceWorkerModule } from "@angular/service-worker";
import { environment } from '../environments/environment';

import { TranslateService } from 'projects/shared/src/lib/services/translate.service';
import { NotificationService } from 'projects/shared/src/lib/services/notification.service';
import { AdminUserRouteGuardService } from 'projects/shared/src/lib/guards/admin-user-route-guard.service';

import { ToastrModule } from 'ngx-toastr';

import { AuthInterceptor, AuthModule, OidcConfigService } from 'angular-auth-oidc-client';

import { AppComponent } from './app.component';
import { NoAccessComponent } from './no-access/no-access.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { SigninRedirectCallbackComponent } from './signin-redirect-callback/signin-redirect-callback.component';
import { SignoutRedirectCallbackComponent } from './signout-redirect-callback/signout-redirect-callback.component';
import { RedirectSilentRenewComponent } from './redirect-silent-renew/redirect-silent-renew.component';

// -------------- Custom Generic Html Components for Ngx-Formly ------------------------------------------------------------------------------------------------------------------------
import { AlSelectTypeComponent } from 'projects/user/src/lib/manage-documents/document/edit-document/generic-form/custom-types/al-select-type/al-select-type.component';
import { AlTextareaTypeComponent } from 'projects/user/src/lib/manage-documents/document/edit-document/generic-form/custom-types/al-textarea-type/al-textarea-type.component';
import { AlFileUploadTypeComponent } from 'projects/user/src/lib/manage-documents/document/edit-document/generic-form/custom-types/al-file-upload-type/al-file-upload-type.component';
import { AlTextInputTypeComponent } from 'projects/user/src/lib/manage-documents/document/edit-document/generic-form/custom-types/al-text-input-type/al-text-input-type.component';
import { AlToggleTypeComponent } from 'projects/user/src/lib/manage-documents/document/edit-document/generic-form/custom-types/al-toggle-type/al-toggle-type.component';
import { AlCheckboxTypeComponent } from 'projects/user/src/lib/manage-documents/document/edit-document/generic-form/custom-types/al-checkbox-type/al-checkbox-type.component';
import { AlRadioTypeComponent } from 'projects/user/src/lib/manage-documents/document/edit-document/generic-form/custom-types/al-radio-type/al-radio-type.component';
import { AlEmailTypeComponent } from 'projects/user/src/lib/manage-documents/document/edit-document/generic-form/custom-types/al-email-type/al-email-type.component';
import { AlNumberTypeComponent } from 'projects/user/src/lib/manage-documents/document/edit-document/generic-form/custom-types/al-number-type/al-number-type.component';
// ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------

// ------------- Squirrel Projects Modules ----------------------------------------------------------------------------------------------------------------------------------------------- 
import { SharedModule } from 'projects/shared/src/lib/shared.module';
import { AdminModule } from 'projects/admin/src/lib/admin.module';
import { UserModule } from 'projects/user/src/projects';
import { PopupsModule } from 'projects/popups/src/lib/popups.module';
// -----------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------

// ---------- Ngrx -------------------------------------------------------------------------------------------------------------------------------------------------------------------------
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';

import { DSTEffects } from 'projects/shared/src/lib/ngrx-store/doc-stereo-type/doc-stereo-type.effects';
import {reducers, metaReducers} from 'projects/shared/src/lib/ngrx-store/app-local-reducer';

// ----------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------

// ------- Ag-Grid ------------------------------------------------------------------------------------------------------------------------------------------------------------------------
import { AgGridModule } from 'ag-grid-angular';
// ----------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------

// -------

import {NgMaterialModule} from 'projects/main/src/app/ng-material.module';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';


import { ProjectSessionEffects } from 'projects/shared/src/lib/ngrx-store/project-session/project-session.effects';
import { ValueConfPageEffects } from 'projects/shared/src/lib/ngrx-store/value-conf-page/value-conf-page.effects';
import { DocumentEffects } from 'projects/shared/src/lib/ngrx-store/document/document.effects';
import { AttributeValueEffects } from 'projects/shared/src/lib/ngrx-store/attribute-value/attribute-value.effects';
import { SquirrelLayoutModule } from 'projects/squirrel-layout/src/projects';
import { UserGroupRolesDialogComponent } from 'projects/admin/src/lib/manage-projects/project-role/user-group-roles-dialog/user-group-roles-dialog.component';

//import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

export function configureAuth(oidcConfigService: OidcConfigService): () => Promise<any> {
  return () => oidcConfigService.withConfig(
    {
      clientId: environment.openIdConnectSettings.client_id,
      scope: environment.openIdConnectSettings.scope,      
      responseType: environment.openIdConnectSettings.response_type,
      stsServer: environment.openIdConnectSettings.authority, 
      autoUserinfo:true,
      silentRenew: true,    
      silentRenewUrl: environment.openIdConnectSettings.silent_redirect_uri,
      redirectUrl: environment.openIdConnectSettings.redirect_uri,
      postLogoutRedirectUri: environment.openIdConnectSettings.post_logout_redirect_uri,
      //useRefreshToken: true, 
      useRefreshToken: false,
      // postLoginRoute: window.location.origin,
       renewTimeBeforeTokenExpiresInSeconds: 30,
      //renewTimeBeforeTokenExpiresInSeconds: 60,
      historyCleanupOff:true,      
      logLevel: 3,
      secureRoutes: [
        environment.identityApi,
        environment.identityAccount,
        environment.identityAdmin,
        environment.projectsApi,
        environment.statusColor,
        environment.userProfile,
        environment.permissions,
        environment.valueConfAttribute,
        environment.valueConfPage,
        environment.genericDocuments,
        environment.documents,
        environment.databaseMigrations,
        environment.attributeValue,
        environment.account,
        environment.projectroles, 
        environment.platformGroupRoles,       
        //environment.groupRoles,
        environment.projectGroupRoles,
        environment.groupRolePermissions,
        environment.projectGroupRolePermissions,
        environment.tenants,
        environment.tenantProjects,
        environment.tenantUsers,
        environment.tenantProjectGroupRoles,
        environment.tenantGroupRolePermissions,
        environment.tenantProjectRoles,
        environment.tenantProjectGroupRolePermissions,
        environment.tenantplatformGroupRoles,

       

        // --------------- Legacy Squirrel Urls --------------------------------------------
        environment.localLegacySquirrelCommonApi,
        environment.localLegacySquirrelV3,
        environment.devLegacySquirrelCommonApi,
        environment.devLegacySquirrelV3,
        environment.squirreltest1LegacySquirrelCommonApi,
        environment.squirreltest1LegacySquirrelV3,
        environment.squirreltest2LegacySquirrelV3,
        environment.sq3devLegacySquirrelCommonApi,
        environment.sq3devLegacySquirrelV3,

        // --------------- Squirrel Management Urls --------------------------------------------
        //environment.localLegacySquirrelCommonApi,
        "https://squirrelmanagementapi-2346weg2erhsdghewww.albert-ing.com/api/SQ3Authentication/addAuthentication"
      ] 
    });
}

export function setupTranslateFactory(translateService: TranslateService) {
  // return () => translateService.use("en");
  const lang = localStorage.getItem('app_lang') as string;
  return () => translateService.use(lang);
}

@NgModule({
  declarations: [
    AppComponent,
    NoAccessComponent,
    PageNotFoundComponent,
    SigninRedirectCallbackComponent,
    SignoutRedirectCallbackComponent,
    RedirectSilentRenewComponent,
    //UserChangePasswordComponent
  ],
  entryComponents:[
    UserGroupRolesDialogComponent
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    AuthModule.forRoot(),   
    
    // EffectsModule.forRoot([DSTEffects]),   
    // StoreModule.forRoot({docstereotypes:dstReducer}),

    EffectsModule.forRoot([DSTEffects,ProjectSessionEffects,DocumentEffects,AttributeValueEffects,ValueConfPageEffects]),   
    StoreModule.forRoot(reducers, {
      metaReducers
    }),
    StoreDevtoolsModule.instrument({
     name: 'Document Stereo Types',
     maxAge:25, // Retains last 25 states
     logOnly:environment.production  // Restrict extension to log-only mode 
    }),
    AppRoutingModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    ServiceWorkerModule.register("./ngsw-worker.js", {
      enabled: environment.production,
      registrationStrategy: "registerImmediately"
    }),
    NgMaterialModule,  
    SharedModule, 
    SquirrelLayoutModule,
    PopupsModule,
    UserModule,
    AdminModule,   
    FormlyModule.forRoot({
      extras: { lazyRender: true },
      types: [
        { name: 'al-select-type', component: AlSelectTypeComponent },
        { name: 'al-textarea-type', component: AlTextareaTypeComponent },
        { name: 'al-file-upload-type', component: AlFileUploadTypeComponent },
        { name: 'al-text-input-type', component: AlTextInputTypeComponent },
        { name: 'al-toggle-type', component: AlToggleTypeComponent },
        { name: 'al-checkbox-type', component: AlCheckboxTypeComponent },
        { name: 'al-radio-type', component: AlRadioTypeComponent },
        { name: 'al-email-type', component: AlEmailTypeComponent },
        { name: 'al-number-type', component: AlNumberTypeComponent }
      ]
    }),  
   // FullCalendarModule,
    FormlyMaterialModule,
    AgGridModule.withComponents([]),    
  ],
  exports: [
    FormsModule, ReactiveFormsModule,
    //AuthModule,
    FormlyModule, FormlyMaterialModule,SharedModule
  ],
  providers: [
    OidcConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: configureAuth,
      deps: [OidcConfigService],
      multi: true
    },
    TranslateService,
    {
      provide: APP_INITIALIZER,
      useFactory: setupTranslateFactory,
      deps: [TranslateService],
      multi: true
    },
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: AuthInterceptor,
    //   multi: true,
    // },
    //AdminUserRouteGuardService,
    { provide: NotificationService, useClass: NotificationService }
  ],
  bootstrap: [AppComponent],
  schemas: [NO_ERRORS_SCHEMA, CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule { }


